import { z } from 'zod';

/**
 * Preferences API Request/Response Validation Schemas
 *
 * Owns the automation-related user preferences surfaced by epic #139:
 * - Auto-Apply vs Classify Only mode
 * - First-run consent timestamp
 * - Pro tier eligibility (derived server-side, never client-settable)
 */

export const AutomationModeSchema = z.enum(['auto_apply', 'classify_only']);
export type AutomationMode = z.infer<typeof AutomationModeSchema>;

/**
 * Per-track auto-apply policy summary shown in the "See what PureBox will auto-apply" modal.
 * Server is source of truth so we can tune thresholds without a frontend release.
 */
export const AutomationPolicySummarySchema = z.object({
  attention: z.object({
    description: z.string(),
  }),
  archive: z.object({
    min_confidence: z.number().min(0).max(1),
    description: z.string(),
  }),
  trash: z.object({
    min_confidence: z.number().min(0).max(1),
    description: z.string(),
  }),
});
export type AutomationPolicySummary = z.infer<typeof AutomationPolicySummarySchema>;

export const AutomationPreferenceSchema = z.object({
  mode: AutomationModeSchema,
  consent_at: z.string().datetime().nullable(),
  is_eligible: z.boolean(),
  policy_summary: AutomationPolicySummarySchema,
});
export type AutomationPreference = z.infer<typeof AutomationPreferenceSchema>;

export const UpdateAutomationRequestSchema = z.object({
  mode: AutomationModeSchema,
});
export type UpdateAutomationRequest = z.infer<typeof UpdateAutomationRequestSchema>;

export const RecordConsentRequestSchema = z.object({}).optional();
export type RecordConsentRequest = z.infer<typeof RecordConsentRequestSchema>;

/**
 * Optional email permissions (issue #906).
 *
 * Lifecycle email and feedback/research email are separate purposes. Both are
 * default-off and independent from essential account email.
 */
export const EMAIL_CONSENT_COPY_VERSION = 'email-consent-v1';

export const EmailConsentPurposeSchema = z.enum(['lifecycle', 'feedback']);
export type EmailConsentPurpose = z.infer<typeof EmailConsentPurposeSchema>;

export const EmailConsentSourceSchema = z.enum([
  'onboarding',
  'results',
  'post_cleanup',
  'settings',
  'import',
  'migration',
]);
export type EmailConsentSource = z.infer<typeof EmailConsentSourceSchema>;

export const EmailConsentPreferenceSchema = z.object({
  consent: z.boolean(),
  consent_at: z.string().datetime().nullable(),
  source: EmailConsentSourceSchema.nullable(),
  copy_version: z.string().nullable(),
  prompt_last_shown_at: z.string().datetime().nullable(),
  prompt_last_dismissed_at: z.string().datetime().nullable(),
  eligible: z.boolean(),
});
export type EmailConsentPreference = z.infer<typeof EmailConsentPreferenceSchema>;

export const EmailPreferencesSchema = z.object({
  lifecycle: EmailConsentPreferenceSchema,
  feedback: EmailConsentPreferenceSchema,
});
export type EmailPreferences = z.infer<typeof EmailPreferencesSchema>;

const EmailPreferenceEventBaseSchema = z.object({
  purpose: EmailConsentPurposeSchema,
  source: EmailConsentSourceSchema,
  copy_version: z.string().min(1).max(64),
});

export const UpdateEmailPreferenceRequestSchema = z.discriminatedUnion('action', [
  EmailPreferenceEventBaseSchema.extend({
    action: z.literal('set_consent'),
    consent: z.boolean(),
  }),
  EmailPreferenceEventBaseSchema.extend({
    action: z.literal('record_impression'),
  }),
  EmailPreferenceEventBaseSchema.extend({
    action: z.literal('dismiss_prompt'),
  }),
]);
export type UpdateEmailPreferenceRequest = z.infer<typeof UpdateEmailPreferenceRequestSchema>;
